import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
    Package,
    CheckCircle,
    Clock,
    Target,
    XCircle,
    BarChart2,
    Calendar,
    AlertTriangle,
    TrendingUp,
    ChevronDown,
    ChevronRight,
    Tag,
    ExternalLink,
    Info
} from 'react-feather';
import { releasesApi } from '../../services/api';
import { getProgressColor } from '../../utils/projectUtils';
import { isDateOnOrBeforeToday } from '../../utils/date';
import { isReleaseReadyForRelease } from '../../utils/releaseUtils';
import { Button } from '../ui/button';
import ReadyForReleaseBadge from '../Releases/ReadyForReleaseBadge';
import { IconButton } from '../ui/IconButton';
import TaskTypeIcon from '../shared/TaskTypeIcon';
import FeatureTagDetailModal from '../FeatureTags/FeatureTagDetailModal';
import './ReleaseManagementStatsTab.css';

const ReleaseManagementStatsTab = ({ projectId, projectName, tasks = [] }) => {
    const navigate = useNavigate();
    const [releases, setReleases] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [expandedRows, setExpandedRows] = useState(new Set());
    const [hideCompletedCancelled, setHideCompletedCancelled] = useState(true);
    const [selectedFeatureTag, setSelectedFeatureTag] = useState(null);
    const [showFeatureModal, setShowFeatureModal] = useState(false);

    useEffect(() => {
        fetchReleases();
    }, [projectId]);

    const fetchReleases = async () => {
        try {
            setLoading(true);
            setError('');
            const data = await releasesApi.getProjectReleases(projectId);
            setReleases(data || []);
        } catch (err) {
            console.error('Error fetching releases:', err);
            setError('Failed to load release data');
        } finally {
            setLoading(false);
        }
    };

    // Calculate aggregate statistics
    const stats = useMemo(() => {
        const totalReleases = releases.length;
        const completedReleases = releases.filter(r => r.status === 'completed').length;
        const inProgressReleases = releases.filter(r => r.status === 'in_progress').length;
        const plannedReleases = releases.filter(r => r.status === 'planned').length;
        const cancelledReleases = releases.filter(r => r.status === 'cancelled').length;
        
        const totalFeatures = releases.reduce((sum, release) => 
            sum + (release.release_feature_tags?.length || 0), 0);
        
        const totalTasks = releases.reduce((sum, release) => 
            sum + (release.insights?.total_tasks || 0), 0);
        
        const completedTasks = releases.reduce((sum, release) => 
            sum + (release.insights?.completed_tasks || 0), 0);
        
        const totalHours = releases.reduce((sum, release) => 
            sum + (parseFloat(release.insights?.total_hours) || 0), 0);
        
        // Calculate average completion percentage
        let totalProgress = 0;
        let releasesWithProgress = 0;
        releases.forEach(release => {
            if (release.insights && release.insights.completion_percentage !== undefined) {
                totalProgress += release.insights.completion_percentage;
                releasesWithProgress++;
            }
        });
        const avgProgress = releasesWithProgress > 0 
            ? Math.round(totalProgress / releasesWithProgress) 
            : 0;
        
        // Calculate overall completion
        const overallCompletion = totalTasks > 0 
            ? Math.round((completedTasks / totalTasks) * 100) 
            : 0;
        
        // Find upcoming and overdue releases
        const now = new Date();
        const upcomingReleases = releases.filter(r => {
            if (!r.target_date || r.status === 'completed' || r.status === 'cancelled') return false;
            return new Date(r.target_date) >= now;
        }).length;
        
        const overdueReleases = releases.filter(r => {
            if (!r.target_date || r.status === 'completed' || r.status === 'cancelled') return false;
            return new Date(r.target_date) < now;
        }).length;
        
        return {
            totalReleases,
            completedReleases,
            inProgressReleases,
            plannedReleases,
            cancelledReleases,
            totalFeatures,
            totalTasks,
            completedTasks,
            totalHours,
            avgProgress,
            overallCompletion,
            upcomingReleases,
            overdueReleases
        };
    }, [releases]);

    // Sort releases by target date descending (most recent first)
    const sortedReleases = useMemo(() => {
        return [...releases].sort((a, b) => {
            const dateA = a.target_date ? new Date(a.target_date) : new Date(0);
            const dateB = b.target_date ? new Date(b.target_date) : new Date(0);
            return dateA - dateB; // ascending: oldest first
        });
    }, [releases]);

    // Optionally hide completed and cancelled releases (default: hidden)
    const visibleReleases = useMemo(() => {
        if (!hideCompletedCancelled) return sortedReleases;
        return sortedReleases.filter(r => r.status !== 'completed' && r.status !== 'cancelled');
    }, [sortedReleases, hideCompletedCancelled]);

    // Check if release is overdue (target date reached or passed, and not completed)
    const isReleaseOverdue = (release) => {
        if (!release.target_date || release.status === 'completed' || release.status === 'cancelled') {
            return false;
        }
        return isDateOnOrBeforeToday(release.target_date);
    };

    // Format date
    const formatDate = (dateString) => {
        if (!dateString) return 'Not set';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    // Get status color
    const getStatusColor = (status) => {
        switch (status) {
            case 'completed': return '#059669';
            case 'in_progress': return '#d97706';
            case 'planned': return '#4b5563';
            case 'cancelled': return '#dc2626';
            default: return '#4b5563';
        }
    };

    // Handle row expansion toggle
    const handleRowExpand = (releaseId, e) => {
        e.stopPropagation();
        setExpandedRows(prev => {
            const newSet = new Set(prev);
            if (newSet.has(releaseId)) {
                newSet.delete(releaseId);
            } else {
                newSet.add(releaseId);
            }
            return newSet;
        });
    };

    // Handle release click (open release details in new tab)
    const handleReleaseClick = (releaseId) => {
        const path = `/projects/${projectId}/releases/${releaseId}`;
        window.open(path, '_blank', 'noopener,noreferrer');
    };

    // Calculate resource impact for a feature (pending tasks by user and unassigned tasks)
    const getFeatureResourceImpact = (featureTagId) => {
        // Get all tasks for this feature tag that are not completed
        const featureTasks = tasks.filter(task => 
            task.feature_tag?.id === featureTagId && 
            task.status !== 'completed'
        );

        // Group by assigned user
        const tasksByUser = {};
        let unassignedCount = 0;

        featureTasks.forEach(task => {
            if (task.assigned_to?.id) {
                const userId = task.assigned_to.id;
                const userName = task.assigned_to.display_name || 'Unknown';
                if (!tasksByUser[userId]) {
                    tasksByUser[userId] = {
                        name: userName,
                        count: 0
                    };
                }
                tasksByUser[userId].count++;
            } else {
                unassignedCount++;
            }
        });

        return {
            tasksByUser: Object.values(tasksByUser),
            unassignedCount
        };
    };

    if (loading) {
        return (
            <div className="release-stats-tab-loading">
                <div className="loading-spinner"></div>
                <p>Loading release statistics...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="release-stats-tab-error">
                <AlertTriangle size={24} />
                <p>{error}</p>
                <Button onClick={fetchReleases}>Retry</Button>
            </div>
        );
    }

    return (
        <div className="release-stats-tab">
            {/* Summary Statistics */}
            <div className="release-stats-summary">
                <div className="stat-card">
                    <div className="stat-icon">
                        <Package size={20} />
                    </div>
                    <div className="stat-content">
                        <div className="stat-value">{stats.totalReleases}</div>
                        <div className="stat-label">Total Releases</div>
                    </div>
                </div>
                
                <div className="stat-card">
                    <div className="stat-icon" style={{ color: '#d97706' }}>
                        <Clock size={20} />
                    </div>
                    <div className="stat-content">
                        <div className="stat-value">{stats.inProgressReleases}</div>
                        <div className="stat-label">In Progress</div>
                    </div>
                </div>
                
                <div className="stat-card">
                    <div className="stat-icon" style={{ color: '#059669' }}>
                        <CheckCircle size={20} />
                    </div>
                    <div className="stat-content">
                        <div className="stat-value">{stats.completedReleases}</div>
                        <div className="stat-label">Completed</div>
                    </div>
                </div>
                
                <div className="stat-card">
                    <div className="stat-icon" style={{ color: '#4b5563' }}>
                        <Target size={20} />
                    </div>
                    <div className="stat-content">
                        <div className="stat-value">{stats.plannedReleases}</div>
                        <div className="stat-label">Planned</div>
                    </div>
                </div>
                
                <div className="stat-card">
                    <div className="stat-icon">
                        <BarChart2 size={20} />
                    </div>
                    <div className="stat-content">
                        <div className="stat-value">{stats.totalFeatures}</div>
                        <div className="stat-label">Total Features</div>
                    </div>
                </div>
                
                <div className="stat-card">
                    <div className="stat-icon">
                        <TrendingUp size={20} />
                    </div>
                    <div className="stat-content">
                        <div className="stat-value">{stats.avgProgress}%</div>
                        <div className="stat-label">Avg Progress</div>
                    </div>
                </div>
                
                <div className="stat-card">
                    <div className="stat-icon" style={{ color: '#6366f1' }}>
                        <Clock size={20} />
                    </div>
                    <div className="stat-content">
                        <div className="stat-value">{stats.totalHours.toFixed(1)}h</div>
                        <div className="stat-label">Total Hours</div>
                    </div>
                </div>
            </div>

            {/* Additional Stats */}
            <div className="release-stats-secondary">
                <div className="secondary-stat">
                    <span className="secondary-stat-label">Total Tasks:</span>
                    <span className="secondary-stat-value">{stats.totalTasks}</span>
                </div>
                <div className="secondary-stat">
                    <span className="secondary-stat-label">Completed Tasks:</span>
                    <span className="secondary-stat-value">{stats.completedTasks}</span>
                </div>
                <div className="secondary-stat">
                    <span className="secondary-stat-label">Overall Completion:</span>
                    <span className="secondary-stat-value">{stats.overallCompletion}%</span>
                </div>
                {stats.upcomingReleases > 0 && (
                    <div className="secondary-stat">
                        <span className="secondary-stat-label">Upcoming:</span>
                        <span className="secondary-stat-value">{stats.upcomingReleases}</span>
                    </div>
                )}
                {stats.overdueReleases > 0 && (
                    <div className="secondary-stat secondary-stat-warning">
                        <span className="secondary-stat-label">Overdue:</span>
                        <span className="secondary-stat-value">{stats.overdueReleases}</span>
                    </div>
                )}
            </div>

            {/* Releases List */}
            <div className="release-stats-list">
                <div className="release-stats-list-header">
                    <h3 className="release-stats-list-title">All Releases</h3>
                    <label className="release-stats-hide-toggle">
                        <input
                            type="checkbox"
                            checked={hideCompletedCancelled}
                            onChange={(e) => setHideCompletedCancelled(e.target.checked)}
                        />
                        <span>Hide completed/cancelled</span>
                    </label>
                </div>
                {releases.length === 0 ? (
                    <div className="release-stats-empty">
                        <Package size={48} />
                        <p>No releases found for this project</p>
                    </div>
                ) : visibleReleases.length === 0 ? (
                    <div className="release-stats-empty">
                        <Package size={48} />
                        <p>No releases to show{hideCompletedCancelled ? ' (completed/cancelled are hidden)' : ''}</p>
                    </div>
                ) : (
                    <div className="release-stats-table">
                        <table>
                            <thead>
                                <tr>
                                    <th style={{ width: '40px' }}></th>
                                    <th>Release</th>
                                    <th>Status</th>
                                    <th>Target Date</th>
                                    <th>Features</th>
                                    <th>Tasks</th>
                                    <th>Hours</th>
                                    <th>Progress</th>
                                </tr>
                            </thead>
                            <tbody>
                                {visibleReleases.map(release => {
                                    const progress = release.insights?.completion_percentage || 0;
                                    const taskCount = release.insights?.total_tasks || 0;
                                    const completedTaskCount = release.insights?.completed_tasks || 0;
                                    const releaseHours = parseFloat(release.insights?.total_hours) || 0;
                                    const featureCount = release.release_feature_tags?.length || 0;
                                    const isExpanded = expandedRows.has(release.id);
                                    const featureTags = release.release_feature_tags || [];
                                    const insightFeatures = release.insights?.feature_tags || [];
                                    const releasePendingStory = insightFeatures.reduce((s, ft) => s + (ft.pending_story ?? 0), 0);
                                    const releasePendingTask = insightFeatures.reduce((s, ft) => s + (ft.pending_task ?? 0), 0);
                                    const releasePendingSubtask = insightFeatures.reduce((s, ft) => s + (ft.pending_subtask ?? 0), 0);
                                    const releasePendingBug = insightFeatures.reduce((s, ft) => s + (ft.pending_bug ?? 0), 0);
                                    const releaseHasPending = releasePendingStory > 0 || releasePendingTask > 0 || releasePendingSubtask > 0 || releasePendingBug > 0;
                                    
                                    return (
                                        <React.Fragment key={release.id}>
                                            <tr className="release-row">
                                                <td onClick={(e) => handleRowExpand(release.id, e)}>
                                                    <button 
                                                        className="expand-button"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleRowExpand(release.id, e);
                                                        }}
                                                    >
                                                        {isExpanded ? (
                                                            <ChevronDown size={16} />
                                                        ) : (
                                                            <ChevronRight size={16} />
                                                        )}
                                                    </button>
                                                </td>
                                                <td>
                                                    <div className="release-name-cell">
                                                        <div className="release-name" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                                                            <span>{release.name}</span>
                                                            <IconButton
                                                                variant="neutral-tertiary"
                                                                className="release-view-icon-button"
                                                                icon={<ExternalLink size={12} />}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleReleaseClick(release.id);
                                                                }}
                                                                title="View release details"
                                                            />
                                                            {isReleaseReadyForRelease(release, tasks) && <ReadyForReleaseBadge />}
                                                        </div>
                                                        <div className="release-version">v{release.version}</div>
                                                    </div>
                                                </td>
                                                <td>
                                                    <span 
                                                        className="release-status-badge"
                                                        style={{ 
                                                            backgroundColor: getStatusColor(release.status) + '20',
                                                            color: getStatusColor(release.status)
                                                        }}
                                                    >
                                                        {release.status.replace('_', ' ')}
                                                    </span>
                                                </td>
                                                <td>
                                                    <div className="release-target-date-cell">
                                                        <span>{formatDate(release.target_date)}</span>
                                                        {isReleaseOverdue(release) && (
                                                            <span className="release-overdue-badge">Overdue</span>
                                                        )}
                                                    </div>
                                                </td>
                                                <td>{featureCount}</td>
                                                <td>
                                                    <div className="release-tasks-cell">
                                                        {taskCount > 0 ? (
                                                            <span>{completedTaskCount}/{taskCount}</span>
                                                        ) : (
                                                            <span>-</span>
                                                        )}
                                                        {releaseHasPending && (
                                                            <div className="release-row-pending-stats">
                                                                {releasePendingStory > 0 && (
                                                                    <span className="release-row-pending-item" title="Story pending">
                                                                        <TaskTypeIcon taskType="Story" size={14} />
                                                                        <span>{releasePendingStory}</span>
                                                                    </span>
                                                                )}
                                                                {releasePendingTask > 0 && (
                                                                    <span className="release-row-pending-item" title="Task pending">
                                                                        <TaskTypeIcon taskType="Task" size={14} />
                                                                        <span>{releasePendingTask}</span>
                                                                    </span>
                                                                )}
                                                                {releasePendingSubtask > 0 && (
                                                                    <span className="release-row-pending-item" title="Subtask pending">
                                                                        <TaskTypeIcon taskType="Subtask" size={14} />
                                                                        <span>{releasePendingSubtask}</span>
                                                                    </span>
                                                                )}
                                                                {releasePendingBug > 0 && (
                                                                    <span className="release-row-pending-item" title="Bug pending">
                                                                        <TaskTypeIcon taskType="Bug" size={14} />
                                                                        <span>{releasePendingBug}</span>
                                                                    </span>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                </td>
                                                <td>
                                                    <span className="release-hours-value">
                                                        {releaseHours > 0 ? `${releaseHours.toFixed(1)}h` : '-'}
                                                    </span>
                                                </td>
                                                <td>
                                                    <div className="release-progress-cell">
                                                        <div className="release-progress-bar">
                                                            <div 
                                                                className="release-progress-fill"
                                                                style={{ 
                                                                    width: `${progress}%`,
                                                                    backgroundColor: getProgressColor(progress)
                                                                }}
                                                            />
                                                        </div>
                                                        <span className="release-progress-text">{progress}%</span>
                                                    </div>
                                                </td>
                                            </tr>
                                            {isExpanded && featureTags.length > 0 && (
                                                <tr className="release-row-expanded">
                                                    <td colSpan="8">
                                                        <div className="release-features-expanded">
                                                            <div className="release-features-header">
                                                                <Tag size={16} />
                                                                <span>Features ({featureTags.length})</span>
                                                            </div>
                                                            <div className="release-features-list">
                                                                {[...featureTags].sort((a, b) => {
                                                                    const progressA = release.insights?.feature_tags?.find(
                                                                        ft => ft.id === a.feature_tag.id
                                                                    )?.completion_percentage || 0;
                                                                    const progressB = release.insights?.feature_tags?.find(
                                                                        ft => ft.id === b.feature_tag.id
                                                                    )?.completion_percentage || 0;
                                                                    // Completed features (100%) go to the end
                                                                    if (progressA === 100 && progressB !== 100) return 1;
                                                                    if (progressA !== 100 && progressB === 100) return -1;
                                                                    return 0;
                                                                }).map(rft => {
                                                                    const featureTag = rft.feature_tag;
                                                                    const featureProgress = release.insights?.feature_tags?.find(
                                                                        ft => ft.id === featureTag.id
                                                                    );
                                                                    const featureProgressPercentage = featureProgress?.completion_percentage || 0;
                                                                    const featureTaskCount = featureProgress?.task_count || 0;
                                                                    const featureCompletedTasks = featureProgress?.completed_tasks || 0;
                                                                    const featureHours = parseFloat(featureProgress?.hours) || 0;
                                                                    const pendingStory = featureProgress?.pending_story ?? 0;
                                                                    const pendingTask = featureProgress?.pending_task ?? 0;
                                                                    const pendingSubtask = featureProgress?.pending_subtask ?? 0;
                                                                    const pendingBug = featureProgress?.pending_bug ?? 0;
                                                                    const hasPending = pendingStory > 0 || pendingTask > 0 || pendingSubtask > 0 || pendingBug > 0;
                                                                    const isIncomplete = featureProgressPercentage < 100;
                                                                    const resourceImpact = isIncomplete ? getFeatureResourceImpact(featureTag.id) : null;
                                                                    
                                                                    return (
                                                                        <div key={rft.id} className="release-feature-item">
                                                                            <div className="release-feature-header">
                                                                                <div 
                                                                                    className="release-feature-color"
                                                                                    style={{ backgroundColor: featureTag.color }}
                                                                                />
                                                                                <span className="release-feature-name">{featureTag.name}</span>
                                                                                <IconButton
                                                                                    variant="neutral-tertiary"
                                                                                    className="release-feature-info-btn"
                                                                                    icon={<Info size={14} />}
                                                                                    onClick={(e) => {
                                                                                        e.stopPropagation();
                                                                                        setSelectedFeatureTag(featureTag);
                                                                                        setShowFeatureModal(true);
                                                                                    }}
                                                                                    title="View feature details"
                                                                                    aria-label="View feature details"
                                                                                />
                                                                            </div>
                                                                            {featureTaskCount > 0 ? (
                                                                                <div className="release-feature-stats">
                                                                                    <div className="release-feature-stats-row">
                                                                                        <span className="release-feature-tasks">
                                                                                            {featureCompletedTasks}/{featureTaskCount} tasks
                                                                                        </span>
                                                                                        {featureHours > 0 && (
                                                                                            <span className="release-feature-hours">
                                                                                                {featureHours.toFixed(1)}h
                                                                                            </span>
                                                                                        )}
                                                                                        <span className="release-feature-percentage">
                                                                                            {featureProgressPercentage.toFixed(0)}%
                                                                                        </span>
                                                                                        {hasPending && (
                                                                                            <div className="release-feature-pending-stats">
                                                                                                {pendingStory > 0 && (
                                                                                                    <span className="release-feature-pending-item" title="Story pending">
                                                                                                        <TaskTypeIcon taskType="Story" size={14} />
                                                                                                        <span>{pendingStory}</span>
                                                                                                    </span>
                                                                                                )}
                                                                                                {pendingTask > 0 && (
                                                                                                    <span className="release-feature-pending-item" title="Task pending">
                                                                                                        <TaskTypeIcon taskType="Task" size={14} />
                                                                                                        <span>{pendingTask}</span>
                                                                                                    </span>
                                                                                                )}
                                                                                                {pendingSubtask > 0 && (
                                                                                                    <span className="release-feature-pending-item" title="Subtask pending">
                                                                                                        <TaskTypeIcon taskType="Subtask" size={14} />
                                                                                                        <span>{pendingSubtask}</span>
                                                                                                    </span>
                                                                                                )}
                                                                                                {pendingBug > 0 && (
                                                                                                    <span className="release-feature-pending-item" title="Bug pending">
                                                                                                        <TaskTypeIcon taskType="Bug" size={14} />
                                                                                                        <span>{pendingBug}</span>
                                                                                                    </span>
                                                                                                )}
                                                                                            </div>
                                                                                        )}
                                                                                    </div>
                                                                                    <div className="release-feature-progress-bar">
                                                                                        <div 
                                                                                            className="release-feature-progress-fill"
                                                                                            style={{ 
                                                                                                width: `${featureProgressPercentage}%`,
                                                                                                backgroundColor: getProgressColor(featureProgressPercentage)
                                                                                            }}
                                                                                        />
                                                                                    </div>
                                                                                </div>
                                                                            ) : (
                                                                                <div className="release-feature-no-tasks">
                                                                                    <span>No tasks present</span>
                                                                                </div>
                                                                            )}
                                                                            {(featureTag.due_date || (isIncomplete && resourceImpact && (resourceImpact.tasksByUser.length > 0 || resourceImpact.unassignedCount > 0))) && (
                                                                                <div className="release-feature-footer">
                                                                                    {isIncomplete && resourceImpact && resourceImpact.tasksByUser.length > 0 && (
                                                                                        <div className="resource-impact-section">
                                                                                            <span className="resource-impact-label">Resources:</span>
                                                                                            <div className="resource-impact-list">
                                                                                                {resourceImpact.tasksByUser.map((user, idx) => (
                                                                                                    <span key={idx} className="resource-impact-item">
                                                                                                        {user.name} ({user.count})
                                                                                                    </span>
                                                                                                ))}
                                                                                            </div>
                                                                                        </div>
                                                                                    )}
                                                                                    <div className="release-feature-footer-row">
                                                                                        {featureTag.due_date && (
                                                                                            <span className="release-feature-due-date">
                                                                                                Due: {formatDate(featureTag.due_date)}
                                                                                            </span>
                                                                                        )}
                                                                                        {isIncomplete && resourceImpact && resourceImpact.unassignedCount > 0 && (
                                                                                            <span className="resource-impact-unassigned">
                                                                                                Unassigned: {resourceImpact.unassignedCount}
                                                                                            </span>
                                                                                        )}
                                                                                    </div>
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {showFeatureModal && selectedFeatureTag && (
                <FeatureTagDetailModal
                    tag={selectedFeatureTag}
                    isOpen={showFeatureModal}
                    onClose={() => {
                        setSelectedFeatureTag(null);
                        setShowFeatureModal(false);
                    }}
                    tasks={tasks.filter(t => t.feature_tag?.id === selectedFeatureTag?.id)}
                    users={[]}
                />
            )}
        </div>
    );
};

export default ReleaseManagementStatsTab;

